<template>
  <v-row no-gutters align-content="start">
    <v-col 
      cols="12" 
      md="6" 
      class="reference-wrapper d-flex"
      @wheel.prevent="wheel">
      <oct-reference-image 
        :referenceId="name + '-reference-image-wrapper'"
        :paletteId="name + '-reference-palette-wrapper'"
        :isLoading="refImgLoading || octDataLoading"
        :refImg="refImg"
        :refFit="refFit"
        :refSize="refSize"
        :refImgLocation="refImgLocation"
        :refImgNotFound="refImgNotFound"
        :sliceCurrentIndex="sliceCurrentIndex"
        :slices="slices"
        :sliceCurrent="sliceCurrent"
        :composites="composites"
        :compositeCurrent="compositeCurrent"
        :sliceBounds="sliceBounds"
        :compositeBounds="compositeBounds"
        :isPlaying="isPlaying"
        :playRate="playRate"
        :drawAllSlices="!showOverlay"
        :showExtension="!showOverlay"
        :showBindingBox="showOverlay"
        :showOverlay="showOverlay"
        :overlayOpacity="overlayOpacity"
        :overlayImg="overlayImg"
        :overlaySize="overlaySize"
        :overlayAnalysis="overlayAnalysis"
        :overlayFoveaOffset="overlayFoveaOffset"
        :showOverlayETDRS="showETDRS"
        :palette="overlayPalette"
        :paletteSize="overlayPaletteSize"
        :paletteMin="overlayMin"
        :paletteMax="overlayMax"
        :paletteStep="overlayStep"
        :isMultiview="true"
        @previous="previousSlice"
        @next="nextSlice"
        @play="play"
        @pause="pause"
        @setPlayRate="setPlayRate"
        @selectSlice="selectSlice"
        @selectComposite="selectComposite" />
    </v-col>
    <v-col 
      cols="12" 
      md="6" 
      class="cirrus-slice-view"
      @wheel.prevent="wheel">
      <oct-slice-view
        :slice-id="name + '-slice-image-wrapper'"
        :fitSize="refFit"
        :imgSize="refFit"
        :img="sliceImg"
        :imgNotFound="sliceImgNotFound"
        :imgLoading="sliceImgLoading"
        :dataLoading="octDataLoading"
        :otherIndex="sliceCurrentIndex"
        :otherCount="sliceCount"
        :sliceEyeMarker="sliceCurrentEyeMarker"
        :strechSegmentation="true"
        :sliceInfo="sliceInfo"
        :layerData="sliceSegmentationLayerData"
        :octColorSettings="colorSettings"
        otherColor="magenta"
        :showSegmentation="showSegmentation"
        :showOpticDiscLine="true"
        :realLocation="sliceLocation"
        :isInBounds="sliceInBounds"
        :cscans="cscans"
        :cscanCurrent="cscanCurrent"
        :cscanCurrentIndex="cscanCurrentIndex"
        @selectCScan="selectCScan"/>
        <div class="image-index-counter" style="display: flex; justify-content: center;">
          <index-counter :index="sliceCurrentIndex" :count="sliceCount" />
        </div>
    </v-col>
    <v-col 
      cols="12" 
      md="6"
      class="cirrus-slice-view"
      @wheel.prevent="wheel">
      <oct-slice-view
        :slice-id="name + '-composite-image-wrapper'"
        :fitSize="flippedFit"
        :imgSize="refFit"
        :img="compositeImg"
        :imgNotFound="compositeImgNotFound"
        :imgLoading="compositeImgLoading"
        :dataLoading="octDataLoading"
        :otherIndex="compositeCurrentIndex"
        :otherCount="compositeCount"
        :sliceEyeMarker="compositeCurrentEyeMarker"
        :strechSegmentation="true"
        :sliceInfo="compositeInfo"
        :layerData="compositeSegmentationLayerData"
        :octColorSettings="colorSettings"
        otherColor="magenta"
        :flippedX="true"
        :isFlipped270="true"
        :eyeMarkerAngle="Math.PI"
        :showSegmentation="showSegmentation"
        :showOpticDiscLine="true"
        :realLocation="sliceLocation"
        :isInBounds="sliceInBounds"
        :cscans="cscans"
        :cscanCurrent="cscanCurrent"
        :cscanCurrentIndex="cscanCurrentIndex"
        @selectCScan="selectCScan"/>
        <div class="image-index-counter" style="display: flex; justify-content: center;">
          <index-counter :index="compositeCurrentIndex" :count="compositeCount" />
        </div>
    </v-col>
    <v-col 
      cols="12"
      md="6"
      class="reference-wrapper d-flex">
      <cscan-view 
        :cscanId="name + '-cscan-image-wrapper'"
        :cscanFit="cscanFit" 
        :cscanImgSize="cscanImgSize" 
        :octColorSettings="colorSettings"
        :cscanImg="cscanImg"
        :cscanImgNotFound="cscanImgNotFound"
        :cscanImgLoading="cscanImgLoading"/>
    </v-col>
  </v-row>
</template>

<script>
import octSliceView from "./OCTSliceView";
import octReferenceImage from "./OCTReferenceImage";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";
import cirrusComparisonViewHelper from "../../mixins/CirrusComparisonViewHelper";
import cscanView from "./CScanView";
import indexCounter from "../IndexCounter";
import { mapWatchers } from "../../../helpers/utils";
import {
  defaultSize,
  getViewSize,
  setSize,
  setFittedSize,
  viewOffset,
  withinViewBounds,
  calculateRealLocation,
  defaultTransform,
  setCirrusRefereceFit,
  setTransformSizes
} from "../../../canvas/geometry";
export default {
  name: "CirrusComparisonViewBodyContent",
  components: {
    octSliceView,
    octReferenceImage,
    cscanView,
    indexCounter
  },
  mixins: [responsiveCanvases, cirrusComparisonViewHelper],
  props: {
    name: String,
    context: Object,
    exams: Array,
    groups: Array
  },
  data() {
    return {
      refSize: defaultSize(),
      refFit: defaultSize(),
      thicknessMapSize: defaultSize(),
      thicknessPaletteSize: defaultSize(),
      overlaySize: defaultSize(),
      overlayPaletteSize: defaultSize(),
      sliceFit: defaultSize(),
      flippedFit: defaultSize(),
      compositeFit: defaultSize(),
      cscanImgSize: defaultSize(),
      cscanSize: defaultSize(),
      cscanFit: defaultSize(),
      transform: defaultTransform()
    };
  },
  methods: {
    updateCanvasSizes() {
      let refId = this.name + "-reference-image-wrapper";
      let rvs = getViewSize(refId);
      setSize(this.refSize, rvs);
      setCirrusRefereceFit(this.refSize, this.sliceBounds, this.refFit);

      let sliceId = this.name + "-slice-image-wrapper";
      let svs = getViewSize(sliceId);
      setFittedSize(svs, svs, this.sliceFit, 15);
      
      // ensure that thickness map has the same aspect ratio as the reference image map
      let size = {
        w: this.refFit.w * this.relativeMapSize.w,
        h: this.refFit.h * this.relativeMapSize.h
      };
      setSize(this.overlaySize, size);

      let paletteId = this.name + "-thickness-map-palette-wrapper";
      let tmps = getViewSize(paletteId);
      setFittedSize(tmps, tmps, this.thicknessPaletteSize, 5);

      let compositeId = this.name + "-composite-image-wrapper";
      let cvs = getViewSize(compositeId);
      setFittedSize(cvs, cvs, this.compositeFit, 15);

      let cscanId = this.name + "-cscan-image-wrapper";
      let evs = getViewSize(cscanId);
      setSize(this.cscanSize, evs);
      
      setSize(this.cscanImgSize, {w: 256, h: 256});
      setFittedSize(this.cscanSize, this.cscanImgSize, this.cscanFit, 35);

      let overlayPaletteId = this.name + "-reference-palette-wrapper";
      let ops = getViewSize(overlayPaletteId);
      setFittedSize(ops, ops, this.overlayPaletteSize, 5);

      setTransformSizes(this.transform, this.refFit, this.refFit);
      this.flippedFit = {h: this.refFit.w, w: this.refFit.h};
    },
    selectComposite(index){
      this.$store.dispatch("selectOCTImageByIndexCompare", {
        keyword: "composite",
        index,
        view: this.name
      });
    },
    selectCScan(index){
      if (this.cscans.length != 0 ) {
        let newIndex = Math.floor(this.cscans.length * index);
        this.$store.dispatch("selectOCTImageByIndexCompare", {
          keyword: "cscan",
          index: newIndex,
          view: this.name
        });
      }
    },
    sliceLocation(x, y, isFlipped) {
      let imgOffset = 0;
      if (!isFlipped) {
        imgOffset = viewOffset(this.refSize, this.refFit);
        if (!withinViewBounds(x, y, this.refSize, imgOffset)) return { x: -1, y: -1 };
        return calculateRealLocation(x, y, imgOffset, this.transform);
      }
      else {
        imgOffset = viewOffset(this.refSize, this.flippedFit);
        if (!withinViewBounds(x, y, this.refSize, imgOffset)) return { x: -1, y: -1 };
        return calculateRealLocation(x, y, imgOffset, this.transform, true);
      }
    },
    sliceInBounds(x, y) {
      return (
        x >= 0 && y >= 0 && x <= this.refFit.w && y <= this.refFit.h
      );
    },
  },
  computed: {
    sliceCount() {
      return this.slices.length;
    },
    composites() {
      if (!this.context) return [];
      return this.$store.getters.getSlices(
        "composite",
        this.context.exSrNo,
        this.context.groupName
      );
    },
    compositeInfo() {
      return this.context && this.context.compositeInfo;
    },
    compositeHasSegmentation() {
      return this.compositeInfo && this.compositeInfo.sliceHasSegmentation;
    },
    compositeCurrent() {
      return this.context && this.context.compositeCurrent;
    },
    compositeCurrentIndex() {
      return this.compositeCurrent && this.compositeCurrent.index;
    },
    compositeBounds() {
      return this.context && this.context.compositeBounds;
    },
    compositeCount() {
      return this.composites.length;
    },
    compositeCurrentEyeMarker() {
      return this.compositeCurrent && this.compositeCurrent.eyeMarker;
    },
    compositeImg() {
      return this.compositeCurrent && this.compositeCurrent.imgHtmlObject;
    },
    compositeImgNotFound() {
      return !!(this.compositeCurrent && this.compositeCurrent.imgNotFound);
    },
    compositeImgLoading() {
      return (
        this.compositeCurrent &&
        (this.compositeCurrent.imgIsLoading || this.compositeCurrent.imgIsQueued)
      );
    },
    cscans() {
      if (!this.context) return [];
      return this.$store.getters.getSlices(
        "cscan",
        this.context.exSrNo,
        this.context.groupName
      );
    },
    cscanInfo() {
      return this.context && this.context.cscanInfo;
    },
    cscanCurrent() {
      return this.context && this.context.cscanCurrent;
    },
    cscanCurrentIndex() {
      return this.cscanCurrent && this.cscanCurrent.index;
    },
    cscanImg() {
      return this.cscanCurrent && this.cscanCurrent.imgHtmlObject;
    },
    cscanImgNotFound() {
      return !!(this.cscanCurrent && this.cscanCurrent.imgNotFound);
    },
    cscanImgLoading() {
      return (
        this.cscanCurrent &&
        (this.cscanCurrent.imgIsLoading || this.cscanCurrent.imgIsQueued)
      );
    },
    compositeSegmentationLayerData() {
      return this.compositeCurrent && this.compositeCurrent.sliceSegmentationLayerData;
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo", "compositeInfo", "cscans", "showOverlay"], function() {
      this.resetView();
      this.updateCanvasSizes();
    })
  },
  mounted() {
    setTimeout(() => this.updateCanvasSizes(), 500);
  }
};
</script>